import {Injectable} from '@angular/core';
import {CurrentLeaseModel} from '@core/model/currentLease.model';
import {BehaviorSubject, EMPTY, from, lastValueFrom, mergeMap, Observable, throwError, toArray} from "rxjs";
import {CurrentDistrictModel} from "@core/model/district.model";
import {CurrentLandlordModel} from "@core/model/landlord.model";
import {LandlordPropertyModel} from '@core/model/landlordProperty.model';
import {MatSelectOption} from '@core/interfaces/mat-select-component-option.interface';
import {LandlordsService} from '@core-services/landlords.service';
import {AuthTokenInterface} from '@core/interfaces/auth-token.interface';
import {JwtHelperService} from '@shared-services/jwt-helper.service';
import {UtilsService} from '@shared-services/utils.service';
import {LocationOffering} from '@core/model/side-nav.model';
import {SessionKey} from "@ui-kit/framework/security/model/session-key.enum";
import {
  FlattenedUserGrant,
  UserGrantedLocation
} from "@ui-kit/framework/security/model/user-features-response.interface";
import {CurrentContextService} from "@ui-kit/framework/security/service/current-context.service";
import {User} from "@ui-kit/framework/security/model/user.model";
import {NavigationUtilsService} from "@ui-kit/framework/util/service/navigation-utils.service";

export type ReducedFlattenedUserGrant = Pick<FlattenedUserGrant,
  'featureCode' |
  'roleId' |
  'roleCode' |
  'rolePrincipleType' |
  'moduleCode' |
  'moduleOfferingTypeCode' |
  'offeringUuid' |
  'platformOfferingTypeCode' |
  'locationUuid' |
  'landlordUuid'
> & { 'moduleName': string };

@Injectable({
  providedIn: 'root'
})
export class ConsoleContextService extends CurrentContextService<UserGrantedLocation> {

  // TODO !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
  // TODO Note sure of these are used or not, retest and remove after app up and running again
  private currentLeaseInternal: CurrentLeaseModel | null = null;
  private currentLease: CurrentLeaseModel | null = null;

  public selectedDistrict = new BehaviorSubject<CurrentDistrictModel | null>(null);
  public selectedLandlord = new BehaviorSubject<CurrentLandlordModel | null>(null);
  public userDistrictsList = new BehaviorSubject<CurrentDistrictModel[] | null>(null);

  constructor(
    private landLordService: LandlordsService,
    private jwtHelper: JwtHelperService,
    private utilsService: UtilsService
  ) {
    super();
  }

  public override get currentUser(): User | null {
    return super.currentUser;
  }

  public override set currentUser(user: User) {
    super.currentUser = user;
  }

  override set userGrants(grants: UserGrantedLocation[]) {
    sessionStorage.removeItem(SessionKey.USER_GRANTS_KEY);
    super.userGrants = grants;
  }

  override get userGrants() {
    return super.userGrants;
  }

  //todo: remove all new keys made
  override reset(resetSession?: boolean): void {
    this.currentLease = null;
    sessionStorage.removeItem(SessionKey.CURRENT_WEB_CLIENT);
    super.reset(resetSession);
  }

  /**
   * Internal method to filter and flatten the user's access grants.
   * This method implements the core logic for retrieving and filtering access grants.
   *
   * @protected
   * @param grants - The user's access grants.
   * @returns {FlattenedUserGrant[]} An array of flattened and filtered user grants.
   */
  private flattenAccessMap<FlattenedUserGrant>(grants: UserGrantedLocation[] | null): FlattenedUserGrant[] {
    grants = grants ?? [];

    return grants?.flatMap(location =>
      location.userGrantedOfferings.flatMap(offering =>
        offering.userGrantedModules.flatMap(module =>
          module.userGrantedRoles.flatMap(role =>
            role.userGrantedFeatures.map(feature => ({
              ...location,
              ...offering,
              ...module,
              ...role,
              ...feature
            } as FlattenedUserGrant))
          )
        )
      ));
  }

  /**
   * Internal method to filter and flatten the user's access grants.
   * This method implements the core logic for retrieving and filtering access grants.
   *
   * @protected
   * @param {function} [filterPredicate] - Optional function to filter the access grants.
   * @returns {FlattenedUserGrant[]} An array of flattened and filtered user grants.
   */
  protected override filteredAccessMap<FlattenedUserGrant>(filterPredicate?: (access: FlattenedUserGrant) => boolean): FlattenedUserGrant[] {
    const allGrants = this.flattenAccessMap(this.userGrants) as FlattenedUserGrant[];
    return filterPredicate ? allGrants.filter(filterPredicate) : allGrants;
  }

  /**
   * Retrieves the access map for admin users with specific filtering and field reduction.
   * This method overrides the base getAccessMap method to add admin-specific logic.
   *
   * @override
   * @param {function} [additionalFilter] - Optional additional filter function to be combined with the admin filter.
   * @returns {ReducedFlattenedUserGrant[]} An array of reduced and filtered admin user grants.
   */
  public override accessMap<ReducedFlattenedUserGrant>(additionalFilter?: (access: ReducedFlattenedUserGrant) => boolean): ReducedFlattenedUserGrant[] {
    const contextFilter = (access: FlattenedUserGrant) =>
      access.landlordUuid === this.getCurrentLandlordId()
      && access.locationUuid === this.getCurrentLocation()?.uuid;

    const accessMap = this.filteredAccessMap(contextFilter).map(access => ({
      featureCode: access.featureCode,
      roleId: access.roleId,
      roleCode: access.roleCode,
      rolePrincipleType: access.rolePrincipleType,
      moduleCode: access.moduleCode,
      moduleName: access.name,
      moduleOfferingTypeCode: access.moduleOfferingTypeCode,
      offeringUuid: access.offeringUuid,
      platformOfferingTypeCode: access.platformOfferingTypeCode,
      locationUuid: access.locationUuid,
      landlordUuid: access.landlordUuid,
    })) as ReducedFlattenedUserGrant[];

    return additionalFilter ? accessMap.filter(additionalFilter) : accessMap;
  }


  public checkUserFeatures(codes: string[], offeringUuid: string): boolean {
    if (!offeringUuid || !codes) {
      return false;
    }

    const filter = (access: FlattenedUserGrant) =>
      access?.offeringUuid?.toLowerCase() === offeringUuid?.toLowerCase()
      && codes?.some(fl => fl?.toLowerCase() === access?.featureCode?.toLowerCase());

    const grants = this.filteredAccessMap(filter);
    return grants?.length > 0;
  }

  public getCurrentLandlord(): CurrentLandlordModel | null {
    if (!this.selectedLandlord.value) {
      this.selectedLandlord.next(JSON.parse(sessionStorage.getItem(SessionKey.CURRENT_LANDLORD)!));
    }
    return this.selectedLandlord.value;
  }

  public landlord(): Observable<CurrentLandlordModel | null> {
    return this.selectedLandlord.asObservable();
  }

  public setCurrentWebClient(landlordId: string): Promise<CurrentLandlordModel | null> {
    const isValid = this.utilsService.isValidUuid(landlordId);
    if (landlordId != null) {
      if (isValid) {
        return lastValueFrom(this.landLordService.getLandlord(landlordId))
          .then((response: CurrentLandlordModel) => {
            sessionStorage.setItem(SessionKey.CURRENT_WEB_CLIENT, JSON.stringify(response));
            sessionStorage.setItem(SessionKey.CURRENT_LANDLORD_ID, response.uuid);
            sessionStorage.setItem(SessionKey.CURRENT_LANDLORD_CODE, response.code);
            return Promise.resolve(response);
          })
          .catch((reason: string) => {
            console.log("error retrieving landlord by uuid", reason);
            return Promise.resolve(null);
          });
      } else {
        return lastValueFrom(this.landLordService.getLandlordWithCode(landlordId))
          .then((response: CurrentLandlordModel) => {
            sessionStorage.setItem(SessionKey.CURRENT_WEB_CLIENT, JSON.stringify(response));
            sessionStorage.setItem(SessionKey.CURRENT_LANDLORD_ID, response.uuid);
            sessionStorage.setItem(SessionKey.CURRENT_LANDLORD_CODE, response.code);
            return Promise.resolve(response);
          })
          .catch((reason: string) => {
            console.log("error retrieving landlord by code", reason);
            return Promise.resolve(null);
          });
      }
    }
    return Promise.resolve(null);
  }

  getCurrentWebClient(): Promise<CurrentLandlordModel | null> {
    if (sessionStorage.getItem(SessionKey.CURRENT_WEB_CLIENT) === null || sessionStorage.getItem(SessionKey.CURRENT_WEB_CLIENT) === undefined) {
      return this.setCurrentWebClient(sessionStorage.getItem(SessionKey.CURRENT_LANDLORD_ID)!);
    }
    return Promise.resolve(JSON.parse(sessionStorage.getItem(SessionKey.CURRENT_WEB_CLIENT)!) as CurrentLandlordModel);
  }

  getCurrentLandlordCode(): string | null {
    return sessionStorage.getItem(SessionKey.CURRENT_LANDLORD_CODE);
  }

  public getUserAccessibleLandlordsFromToken(): Promise<MatSelectOption[]> {

    let token: AuthTokenInterface = this.jwtHelper.decodeToken(sessionStorage.getItem(SessionKey.TOKEN_KEY)!) as AuthTokenInterface;
    return lastValueFrom(from(token['landlord-ids'])
      .pipe(mergeMap(landlordId => this.landLordService.getLandlord(landlordId)), toArray()))
      .then((allResponses) => {
        return allResponses.map(
          (landlord: CurrentLandlordModel) => {
            let response: MatSelectOption = {name: landlord.companyName, value: landlord.uuid}
            return response;
          });
      });
  }

  setCurrentLocation(location: LandlordPropertyModel) {
    sessionStorage.setItem(SessionKey.CURRENT_LANDLORD_PROPERTY, JSON.stringify(location));
  }

  getCurrentLocation(): LandlordPropertyModel | null {
    if (sessionStorage.getItem(SessionKey.CURRENT_LANDLORD_PROPERTY) != null) {
      return JSON.parse(sessionStorage.getItem(SessionKey.CURRENT_LANDLORD_PROPERTY)!) as LandlordPropertyModel;
    }
    return null;
  }

  // setCurrentUserId(userId: number) {
  //   sessionStorage.setItem(SessionKey.CURRENT_USER_ID, userId.toString());
  // }
  //
  // getCurrentUserId(): number | null {
  //   return parseInt(sessionStorage.getItem(SessionKey.CURRENT_USER_ID)!);
  // }

  getCurrentLandlordId(): string | null {
    return sessionStorage.getItem(SessionKey.CURRENT_LANDLORD_ID);
  }

  // setCurrentLandlordId(clientId: string) {
  //   sessionStorage.setItem(SessionKey.CURRENT_LANDLORD_ID, clientId);
  // }

  setCurrentOfferingId(offeringId: string) {
    sessionStorage.setItem(SessionKey.CURRENT_OFFERING_ID, offeringId);
  }

  getCurrentOfferingId(): string | null {
    if (sessionStorage.getItem(SessionKey.CURRENT_OFFERING_ID) != null) {
      return sessionStorage.getItem(SessionKey.CURRENT_OFFERING_ID)!;
    }
    return null;
  }

  getCurrentLocationOffering(offeringId: string): LocationOffering | null {
    return this.getCurrentLocation()?.locationOfferings?.find(offering => offering.uuid === offeringId) ?? null;
  }

  updateLocationOfferings(res: LocationOffering): void {
    let currentLocation: LandlordPropertyModel | null = this.getCurrentLocation();
    if (currentLocation?.locationOfferings) {
      let index: number = currentLocation.locationOfferings!.findIndex((offering) => offering.uuid == res.uuid);
      if (index >= 0) {
        currentLocation.locationOfferings![index] = res;
      } else {
        currentLocation.locationOfferings!.push(res);
      }
      this.setCurrentLocation(currentLocation);
    }
  }

  refreshCurrentLocation(): Promise<LandlordPropertyModel> {
    return lastValueFrom(this.landLordService.getLandlordProperty(this.getCurrentLandlord()!.uuid, this.getCurrentLocation()!.uuid))
      .then((res: LandlordPropertyModel) => {
        this.setCurrentLocation(res);
        return Promise.resolve(res);
      });
  }

  setAllowedLocationsForLandlord(allowedLocations: string[]): void {
    sessionStorage.setItem(SessionKey.ALLOWED_LANDLORD_LOCATIONS, JSON.stringify(allowedLocations));
  }
}
